import React from 'react';
import sharedStyles from './ManagerShared.module.css';
import styles from './ManagerTacticalConsistency.module.css';

export default function ManagerTacticalConsistency({ consistency }) {
    if (!consistency) return null;

    const { totalGames, wins, draws, losses } = consistency;
    const total = (wins || 0) + (losses || 0) + (draws || 0) || totalGames || 1;

    const winPct  = Math.round((wins   / total) * 100);
    const lossPct = Math.round((losses / total) * 100);
    const drawPct = Math.max(0, 100 - winPct - lossPct);

    // Build conic-gradient stops
    // Rotation starts at -75deg (top becomes the "start" anchor, which we rotate via CSS)
    // Order: green (wins), pink (losses), grey (draws)
    const conicGradient = `conic-gradient(
        rgba(0,168,84,1.0) 0% ${winPct}%,
        rgba(229,57,53,1.0) ${winPct}% ${winPct + lossPct}%,
        rgba(80,79,89,1.0) ${winPct + lossPct}% 100%
    )`;

    // Label positions (degrees, clockwise from top)
    const getPct = (startPct, length) => startPct + length / 2;

    const labelAngle = (pct) => {
        // angle in degrees from top (conic starts at top after rotation)
        const deg = (pct / 100) * 360 - 75; // account for -75deg rotation offset
        return (deg * Math.PI) / 180;
    };

    const LABEL_R = 111; // radius from center for labels (px) - midpoint of 180px and 265px circles
    const CENTER  = 132.5; // half of the 265px circle

    const winMidPct   = getPct(0, winPct);
    const lossMidPct  = getPct(winPct, lossPct);
    const drawMidPct  = getPct(winPct + lossPct, drawPct);

    const getXY = (midPct) => {
        const angle = labelAngle(midPct);
        return {
            x: CENTER + LABEL_R * Math.sin(angle),
            y: CENTER - LABEL_R * Math.cos(angle),
        };
    };

    const winPos  = getXY(winMidPct);
    const lossPos = getXY(lossMidPct);
    const drawPos = getXY(drawMidPct);
 
    return (
        <section className={sharedStyles.section}>
            <h2 className={sharedStyles.sectionTitle}>Tactical Consistency</h2>
            <div className={styles.container}>
                <div className={styles.chartWrapper}>
                    <div className={styles.chartArea}>
                        <svg
                            viewBox="0 0 265 265"
                            className={styles.chartSvg}
                        >
                            {/* Outer donut segments, rotated -165deg to match CSS conic gradient offset */}
                            <g transform="rotate(-165, 132.5, 132.5)">
                                {/* Win segment (green) */}
                                <circle
                                    cx="132.5"
                                    cy="132.5"
                                    r="111.25"
                                    fill="none"
                                    stroke="#00A854"
                                    strokeWidth="42.5"
                                    strokeDasharray={`${(winPct / 100) * 699.004} 699.004`}
                                    strokeDashoffset="0"
                                />
                                {/* Loss segment (red) */}
                                <circle
                                    cx="132.5"
                                    cy="132.5"
                                    r="111.25"
                                    fill="none"
                                    stroke="#E53935"
                                    strokeWidth="42.5"
                                    strokeDasharray={`${(lossPct / 100) * 699.004} 699.004`}
                                    strokeDashoffset={`-${(winPct / 100) * 699.004}`}
                                />
                                {/* Draw segment (grey) */}
                                <circle
                                    cx="132.5"
                                    cy="132.5"
                                    r="111.25"
                                    fill="none"
                                    stroke="#504F59"
                                    strokeWidth="42.5"
                                    strokeDasharray={`${(drawPct / 100) * 699.004} 699.004`}
                                    strokeDashoffset={`-${((winPct + lossPct) / 100) * 699.004}`}
                                />
                            </g>

                            {/* Inner concentric segmented rings */}
                            <g transform="rotate(-165, 132.5, 132.5)">
                                {/* Win inner segment (green) */}
                                <circle
                                    cx="132.5"
                                    cy="132.5"
                                    r="72.5"
                                    fill="none"
                                    stroke="rgba(0, 168, 84, 0.50)"
                                    strokeWidth="35"
                                    strokeDasharray={`${(winPct / 100) * 455.531} 455.531`}
                                    strokeDashoffset="0"
                                />
                                {/* Loss inner segment (red) */}
                                <circle
                                    cx="132.5"
                                    cy="132.5"
                                    r="72.5"
                                    fill="none"
                                    stroke="rgba(229, 57, 53, 0.50)"
                                    strokeWidth="35"
                                    strokeDasharray={`${(lossPct / 100) * 455.531} 455.531`}
                                    strokeDashoffset={`-${(winPct / 100) * 455.531}`}
                                />
                                {/* Draw inner segment (grey) */}
                                <circle
                                    cx="132.5"
                                    cy="132.5"
                                    r="72.5"
                                    fill="none"
                                    stroke="rgba(60, 60, 60, 0.50)"
                                    strokeWidth="35"
                                    strokeDasharray={`${(drawPct / 100) * 455.531} 455.531`}
                                    strokeDashoffset={`-${((winPct + lossPct) / 100) * 455.531}`}
                                />
                            </g>

                            {/* Donut hole (dark center) */}
                            <circle
                                cx="132.5"
                                cy="132.5"
                                r="55"
                                fill="#0c0c0e"
                            />

                            {/* Center Win Percentage Text */}
                            <text
                                x="132.5"
                                y="132.5"
                                textAnchor="middle"
                                dominantBaseline="central"
                                className={styles.centerText}
                            >
                                {winPct}%
                            </text>

                            {/* Floating percentage labels */}
                            {winPct > 8 && (
                                <text
                                    x={winPos.x}
                                    y={winPos.y}
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    className={styles.segLabel}
                                >
                                    {winPct}%
                                </text>
                            )}
                            {lossPct > 8 && (
                                <text
                                    x={lossPos.x}
                                    y={lossPos.y}
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    className={styles.segLabel}
                                >
                                    {lossPct}%
                                </text>
                            )}
                            {drawPct > 8 && (
                                <text
                                    x={drawPos.x}
                                    y={drawPos.y}
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    className={styles.segLabel}
                                >
                                    {drawPct}%
                                </text>
                            )}
                        </svg>
                    </div>
                </div>

                {/* Legend */}
                <div className={styles.legendContainer}>
                    <div className={styles.legendItem}>
                        <span className={`${styles.legendBox} ${styles.winBox}`} />
                        <span className={styles.legendText}>WON</span>
                    </div>
                    <div className={styles.legendItem}>
                        <span className={`${styles.legendBox} ${styles.lossBox}`} />
                        <span className={styles.legendText}>LOST</span>
                    </div>
                    <div className={styles.legendItem}>
                        <span className={`${styles.legendBox} ${styles.drawBox}`} />
                        <span className={styles.legendText}>DRAW</span>
                    </div>
                </div>
            </div>
        </section>
    );
}
